// Merging the search tiers.
//
// Lexical search (search.js) and vector search (match_nodes in SQL) fail in opposite
// directions. Lexical is certain when the words are there and blind when they are not:
// "utensils" will never find "silverware". Vector search reads meaning and is vague about
// specifics — it will happily rank "mallet" alongside the item literally called "hammer",
// and it cannot be trusted with a rare word, a model number or a made-up name. Running both
// and combining them is not a hedge, it is the standard answer: the failure modes do not
// overlap, so the union is better than either.
//
// THERE ARE FOUR LISTS, NOT TWO. Two things carry vectors: a node's own name, and the terms of a
// closed tag vocabulary. One query vector is matched against item names, against location names,
// and against that vocabulary, because a query can be about the thing, about where it is, or
// about what kind of thing it is — and those are different questions with different answers. The
// four resulting rankings get fused here. What does NOT happen is concatenating a node's name
// with its location path before embedding: see the migration for why that was tried and
// abandoned, and note that the tag prompt in vlm.js exists to keep the same contamination out of
// the categories.
//
// HOW THEY ARE COMBINED, and why not the obvious way. The tempting approach is to put both
// scores on one scale and average them. That does not work: the lexical scorer's totals are
// unbounded and tier-shaped, cosine similarity is bounded in [-1, 1], and blending them makes
// whichever has the bigger numbers dominate while the other becomes noise. Reciprocal Rank
// Fusion sidesteps it by throwing the scores away and using only each item's POSITION in each
// list:
//
//     score(item) = Σ over lists  weight / (k + rank_in_that_list)
//
// k = 60 comes from the 2009 paper that introduced it and is what Elasticsearch, OpenSearch,
// Weaviate, Qdrant, Milvus and Vespa all still ship; it is flat anywhere in 30–100. The
// useful property falls out of the formula: appearing in BOTH lists beats topping one of
// them, which is exactly the behaviour wanted here. An item whose label you typed exactly
// will be first lexically and near the top semantically, and nothing that merely means
// something similar can catch it.

// Rank constant. Small k weights the top of each list more aggressively; large k flattens it.
const RRF_K = 60;

// Fuse ranked lists of ids into one ranked list.
//
// `rankings` is an array of arrays, each already ordered best-first, and each containing ids.
// Ties are broken toward the EARLIER list, so callers should pass the list they trust most
// first: two items with identical fused scores should be ordered by the retriever that is
// certain rather than the one that is guessing.
export function fuseRankings(rankings, options = {}) {
  const k = options.k ?? RRF_K;
  const weights = options.weights || [];
  const scores = new Map(); // id -> { score, listIndex, rank }

  (rankings || []).forEach((ranking, listIndex) => {
    const weight = weights[listIndex] ?? 1;
    (ranking || []).forEach((id, index) => {
      const rank = index + 1; // RRF is 1-indexed
      const contribution = weight / (k + rank);
      const existing = scores.get(id);
      if (existing) {
        existing.score += contribution;
      } else {
        scores.set(id, { score: contribution, listIndex, rank });
      }
    });
  });

  return [...scores.entries()]
    .sort((a, b) =>
      b[1].score - a[1].score
      || a[1].listIndex - b[1].listIndex
      || a[1].rank - b[1].rank)
    .map(([id]) => id);
}

// The same thing over records rather than ids, which is what the search box needs: it holds
// item objects from the lexical pass and only ids from the semantic one, and wants objects
// back. A record found by only the semantic tier is resolved through `lookup`; if it cannot
// be resolved it is dropped, because an id the client knows nothing about cannot be rendered.
//
// Pass the lexical list first — see the tie-breaking note above.
export function fuseRecords(lists, options = {}) {
  const idOf = options.idOf || ((record) => record.id);
  const lookup = options.lookup;
  const known = new Map();

  const rankings = (lists || []).map((list) => (list || []).map((entry) => {
    // Each list holds either records or bare ids, so that the semantic tier can pass
    // match_nodes rows straight through.
    const id = typeof entry === "string" ? entry : idOf(entry);
    if (typeof entry !== "string" && !known.has(id)) known.set(id, entry);
    return id;
  }));

  const out = [];
  for (const id of fuseRankings(rankings, options)) {
    const record = known.get(id) || (lookup ? lookup(id) : null);
    if (record) out.push(record);
  }
  return out;
}

// Whether a query is worth spending a network round trip and an embedding call on. One or two
// characters cannot express an idea, and the lexical tier is already narrowing as you type.
export function isSemanticWorthwhile(query) {
  return normalizedLength(query) >= 3;
}

function normalizedLength(query) {
  return String(query || "").trim().length;
}

// ── Turning match_nodes rows into ranked lists ───────────────────────────────

// Split the match_nodes rows by kind. Items and locations are separate lists because they
// answer different questions: an item match is a result, a location match is a hint about where
// results might be.
export function rankMatches(rows, tagRows) {
  const items = [];
  const locations = [];
  for (const row of rows || []) {
    // Number(null) is 0 rather than NaN, so null has to be excluded before the coercion: a
    // missing similarity is not a similarity of zero, and treating it as one would rank a row
    // the search did not score alongside rows it did.
    const raw = row?.similarity;
    const similarity = raw === null || raw === undefined ? NaN : Number(raw);
    if (!Number.isFinite(similarity)) continue;
    const entry = { id: row.id, similarity };
    (row.kind === "location" ? locations : items).push(entry);
  }

  // match_tags rows, keyed by slug rather than id: these describe KINDS of thing, and the same
  // kind applies to many nodes.
  const tags = new Map();
  for (const row of tagRows || []) {
    const slug = typeof row?.slug === "string" ? row.slug : "";
    const raw = row?.similarity;
    const similarity = raw === null || raw === undefined ? NaN : Number(raw);
    if (!slug || !Number.isFinite(similarity)) continue;
    const existing = tags.get(slug);
    if (existing === undefined || similarity > existing) tags.set(slug, similarity);
  }

  return { items, locations, tags };
}

function rankedBySimilarity(entries) {
  return [...entries].sort((a, b) => b.similarity - a.similarity).map((entry) => entry.id);
}

// How far up the tree to look for a matching ancestor. Generous, and bounded because a parent
// cycle is possible in the schema — see the cycle guard in the ancestor-walk migration.
const MAX_ANCESTOR_DEPTH = 24;

// Items that are BOTH plausible in themselves AND inside a location the query also describes.
// This is the answer to "camping utensils": neither half of that query names the answer on its
// own, and the item called "silverware" inside "Camping gear" satisfies both halves at once.
//
// CONJUNCTIVE ON PURPOSE. The obvious implementation is to take every item inside a matching
// location and add it to the results, and it is worse than doing nothing: searching "camping
// utensils" would enter every tent peg in Camping gear as a highly-ranked result, and since RRF
// scores by position rather than by score, an arbitrary tent peg would outrank a real match
// that only one other list found. Requiring the item to clear the similarity floor on its own
// text as well means this list can only ever REORDER things another list already found. It
// cannot introduce anything, so it cannot introduce anything irrelevant.
//
// `parentOf` maps a node id to its parent's id, for items and locations alike.
function rankedByLocationAgreement(items, locations, parentOf) {
  if (!locations.length || !items.length) return [];
  const locationSimilarity = new Map(locations.map((entry) => [entry.id, entry.similarity]));
  const scored = [];

  for (const item of items) {
    // The nearest matching ancestor is not necessarily the best one: an item in
    // "Camping gear > Mess kit" should get credit from whichever of the two the query actually
    // described, so the walk takes the strongest match rather than the first.
    let bestLocation = 0;
    let cursor = parentOf.get(item.id);
    for (let depth = 0; cursor && depth < MAX_ANCESTOR_DEPTH; depth += 1) {
      const similarity = locationSimilarity.get(cursor);
      if (similarity > bestLocation) bestLocation = similarity;
      cursor = parentOf.get(cursor);
    }
    if (bestLocation > 0) scored.push({ id: item.id, score: bestLocation * item.similarity });
  }

  // Multiplying the two is legitimate here and nowhere else in this file: both are cosine
  // similarities from the same model on the same scale, so their product means something. It
  // still leaves as a rank, because a rank is all RRF accepts.
  return scored.sort((a, b) => b.score - a.score).map((entry) => entry.id);
}

// Sanity bound on the tag list. A category genuinely can match a lot of things — searching
// "cutlery" in a well-tagged house SHOULD return every fork — so this is not there to keep the
// list short, only to stop one very broad tag turning into an unbounded render.
const MAX_TAG_MATCHES = 60;

// Items whose TAGS say they are the kind of thing being asked for, regardless of how their own
// label is worded. This is the list that fixes the measured failure: against the query
// "silverware" the label "Beige plastic camping spork (fork-spoon utensil)" scores 0.337, below
// three things that are not silverware, because the noun is outvoted by the adjectives. Its
// `cutlery` tag is not.
//
// THIS LIST MAY INTRODUCE ITEMS, unlike the location-agreement list above, and the difference is
// the point rather than an inconsistency. Location agreement is a claim about an item's
// NEIGHBOURHOOD — "something near this matched" — which is true of every tent peg in Camping
// gear and so cannot be allowed to add anything. A tag is a claim about the ITEM: "this is
// cutlery" meeting "this query means cutlery" is direct evidence about the thing itself. If tags
// could only reorder, they would be unable to surface the item whose own label is uninformative,
// which is the entire reason they exist.
//
// Ordered by the strength of the query-to-tag match, and where an item carries several matching
// tags the best one speaks for it.
function rankedByTagAgreement(pool, tagSimilarity) {
  if (!tagSimilarity || !tagSimilarity.size) return [];
  const scored = [];
  for (const record of pool || []) {
    const tags = Array.isArray(record?.tags) ? record.tags : null;
    if (!tags || !tags.length) continue;
    let best = 0;
    for (const tag of tags) {
      const similarity = tagSimilarity.get(tag);
      if (similarity > best) best = similarity;
    }
    if (best > 0) scored.push({ id: record.id, score: best });
  }
  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_TAG_MATCHES)
    .map((entry) => entry.id);
}

// Relative trust in each list, in the order they are passed below. Reasoned, not measured:
//
//   lexical   1.00  certain when it fires, and the thing users expect to just work
//   semantic  1.00  the node's own name, which is the whole point of the feature
//   tags      0.80  direct evidence about the item, but one step removed — a category is
//                   coarser than a name, so a category match should not outrank one
//   location  0.50  a tie-breaker between things the other lists already found, by construction
//
// The ordering of the weights matters as much as the values: an item found by two lists beats
// one that tops a single list, so a tagged item whose name also half-matches rises above both a
// name-only and a tag-only match. That is the behaviour wanted, and it falls out of RRF rather
// than needing to be arranged.
//
// Worth knowing before adding a fifth: the hybrid-search literature reports a "weakest link"
// effect where an extra retrieval path makes results WORSE rather than better, so a new list
// needs measuring against real queries rather than assuming it helps.
export const DEFAULT_ITEM_WEIGHTS = [1, 1, 0.8, 0.5];

// Keep the order the caller already sees, and append what the later tiers found underneath.
//
// WHY THIS IS AN OPTION AND NOT A FIX. Lexical results paint immediately; the vector tiers land
// up to a second later and re-sort the list, which on a phone means the row under your thumb
// moves as you reach for it. Reordering is not wrong — it is more relevant — but a list that
// rearranges after you have started reading it costs more than the better ordering gains. This
// keeps every record the first paint showed exactly where it was and lets genuinely new finds
// arrive below the fold, so nothing on screen moves.
//
// It does give something up: the semantic tiers can no longer promote a lexical hit, so the
// location-agreement list stops being able to lift "silverware" for the query "camping
// utensils". That case survives anyway, because a query naming no item lexically has nothing to
// hold in place and every result is an append.
function stableAppend(lexical, fused) {
  const lexicalIds = new Set((lexical || []).map((record) => record.id));
  const appended = (fused || []).filter((record) => !lexicalIds.has(record.id));
  return [...(lexical || []), ...appended];
}

// The item results for a query: the lexical ranking fused with what the vector search found.
// `pool` is what the caller is allowed to show — a location-scoped view passes only its own
// rows, so a semantic hit from elsewhere in the tree is dropped rather than leaking into a
// scoped list.
export function fuseItemResults({ lexical, matches, pool, parentOf, weights, stableOrder }) {
  const items = matches?.items || [];
  const locations = matches?.locations || [];
  const tagSimilarity = matches?.tags || null;
  if (!items.length && !locations.length && !tagSimilarity?.size) return lexical;
  const byId = new Map((pool || []).map((record) => [record.id, record]));
  const fused = fuseRecords(
    [
      lexical,
      rankedBySimilarity(items),
      rankedByTagAgreement(pool, tagSimilarity),
      rankedByLocationAgreement(items, locations, parentOf || new Map()),
    ],
    {
      weights: weights || DEFAULT_ITEM_WEIGHTS,
      lookup: (id) => byId.get(id) || null,
    },
  );
  return stableOrder ? stableAppend(lexical, fused) : fused;
}

// There was a fuseLocationResults beside this, ranking locations as results in their own right. No
// screen returns locations from a search any more (see the note in src/search.js), so it went with them.
// The location vectors are still fetched and still matter: rankedByLocationAgreement above uses them to
// promote an item that sits inside a place the query describes.
